import React, { useState } from 'react';
import ApiService from '../../../services/api';
import { getToken } from '../../../utils/auth';
import { useNotification } from '../../../context/NotificationContext';

interface TagSearchResult {
  id: number;
  name: string;
  value: any;
  total_score: number;
}

interface TagDefinitionSearchResult {
  id: number;
  name: string;
  description?: string;
  total_score: number;
}

const TagSearch = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [tagResults, setTagResults] = useState<TagSearchResult[]>([]);
  const [definitionResults, setDefinitionResults] = useState<TagDefinitionSearchResult[]>([]);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!query.trim()) {
      showNotification('Please enter a search term', 'warning');
      return;
    }

    setLoading(true);
    setTagResults([]);
    setDefinitionResults([]);

    try {
      const token = getToken();
      if (!token) {
        showNotification('Authentication required', 'error');
        setLoading(false);
        return;
      }

      const api = new ApiService(token);
      
      try {
        // Search tags
        const tagsData = await api.searchTags(query);
        setTagResults(Array.isArray(tagsData) ? tagsData : []);
        
        // Search tag definitions
        const definitionsData = await api.searchTagDefinitions(query);
        setDefinitionResults(Array.isArray(definitionsData) ? definitionsData : []);
        
        // Show notification if no results
        if (
          (!Array.isArray(tagsData) || tagsData.length === 0) && 
          (!Array.isArray(definitionsData) || definitionsData.length === 0)
        ) {
          showNotification('No results found', 'info');
        }
      } catch (error) {
        console.error('Search error:', error);
        showNotification('Error searching tags', 'error');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      showNotification('Error with authentication', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return 'Complex object';
      }
    }
    
    return String(value);
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Search Tags</h2>
      
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for tags or tag definitions..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tags Results */}
          <div>
            <h3 className="text-lg font-medium mb-3">Tags</h3>
            {tagResults.length > 0 ? (
              <div className="space-y-3">
                {tagResults.map((tag) => (
                  <div key={tag.id} className="p-4 border border-gray-200 rounded-md hover:shadow-md transition-shadow">
                    <div className="font-medium text-blue-600">{tag.name}</div>
                    <div className="mt-1 text-sm text-gray-600">
                      <div className="mt-1">
                        <span className="font-medium">Value:</span>{' '}
                        <span className="font-mono text-xs bg-gray-100 p-1 rounded">
                          {formatValue(tag.value)}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Score: {tag.total_score}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 italic">No tags found</div>
            )}
          </div>

          {/* Tag Definitions Results */}
          <div>
            <h3 className="text-lg font-medium mb-3">Tag Definitions</h3>
            {definitionResults.length > 0 ? (
              <div className="space-y-3">
                {definitionResults.map((def) => (
                  <div key={def.id} className="p-4 border border-gray-200 rounded-md hover:shadow-md transition-shadow">
                    <div className="font-medium text-blue-600">{def.name}</div>
                    {def.description && (
                      <div className="mt-1 text-sm text-gray-600">
                        {def.description}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-500">
                      Score: {def.total_score}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 italic">No tag definitions found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagSearch; 